import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from './context';
import { toast } from 'sonner';
import DashboardShell from './DashboardShell';
import {
  Plus, Search, UserCheck, UserX, Filter, MoreHorizontal,
  Mail, Phone, GraduationCap, BookOpen, Edit, Trash2
} from './components/DeskeyIcons';
import { Card, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { Avatar, AvatarFallback } from './components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Skeleton } from './components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTeachers() {
  const { user, token } = useApp();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ email: '', full_name: '', password: 'teacher123' });
  const [creating, setCreating] = useState(false);

  const fetchTeachers = async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.get(`${API}/users`, { params: { tenant_id: user.tenant_id, role: 'teacher' } , headers });
      setTeachers(res.data);
    } catch { toast.error('Failed to load teachers'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTeachers(); }, [user?.tenant_id]);

  const createTeacher = async () => {
    if (!form.email || !form.full_name) { toast.error('All fields required'); return; }
    setCreating(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.post(`${API}/users`, { ...form, role: 'teacher', tenant_id: user.tenant_id }, { headers });
      toast.success('Teacher created successfully');
      setCreateOpen(false);
      setForm({ email: '', full_name: '', password: 'teacher123' });
      fetchTeachers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create teacher');
    } finally { setCreating(false); }
  };

  const deactivateTeacher = async (tid) => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.delete(`${API}/users/${tid}`, { headers });
      toast.success('Teacher deactivated');
      fetchTeachers();
    } catch { toast.error('Failed to deactivate'); }
  };

  const filtered = teachers.filter(t =>
    t.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    t.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardShell title="Teachers" breadcrumb="Admin Dashboard">
      <div className="space-y-4 fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'hsl(var(--muted-foreground))' }} />
            <Input placeholder="Search teachers..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9" data-testid="teacher-search-input" />
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-teacher-button" style={{ background: 'hsl(var(--primary))', color: 'white', borderRadius: '0.75rem' }}>
                <Plus className="w-4 h-4 mr-2" /> Add Teacher
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md rounded-2xl">
              <DialogHeader>
                <DialogTitle>Add New Teacher</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-sm mb-1.5 block">Full Name</Label>
                  <Input placeholder="Dr. Jane Smith" value={form.full_name}
                    onChange={e => setForm(f => ({ ...f, full_name: e.target.value , headers }))} />
                </div>
                <div>
                  <Label className="text-sm mb-1.5 block">Email</Label>
                  <Input type="email" placeholder="teacher@school.edu" value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-sm mb-1.5 block">Initial Password</Label>
                  <Input value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
                </div>
                <Button onClick={createTeacher} disabled={creating} className="w-full"
                  style={{ background: 'hsl(var(--primary))', color: 'white', borderRadius: '0.75rem' }}>
                  {creating ? 'Creating...' : 'Create Teacher'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            { label: 'Total Teachers', value: teachers.length, color: 'hsl(var(--chart-1))' },
            { label: 'Active', value: teachers.filter(t => t.status === 'active').length, color: 'hsl(var(--success))' },
            { label: 'Inactive', value: teachers.filter(t => t.status !== 'active').length, color: 'hsl(var(--destructive))' },
          ].map(s => (
            <div key={s.label} className="kpi-card">
              <p className="kpi-label">{s.label}</p>
              <p className="kpi-number" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Teachers Table/Cards */}
        {loading ? (
          <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <GraduationCap className="empty-state-icon" />
            <p className="text-sm font-medium">No teachers found</p>
            <p className="text-xs mt-1">Add your first teacher to get started</p>
          </div>
        ) : (
          <Card className="rounded-2xl border" style={{ borderColor: 'hsl(var(--border))' }}>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full desky-table">
                  <thead>
                    <tr>
                      <th className="text-left">Teacher</th>
                      <th className="text-left hidden sm:table-cell">Email</th>
                      <th className="text-left hidden md:table-cell">Role</th>
                      <th className="text-left">Status</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(t => (
                      <tr key={t.id}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <Avatar className="w-8 h-8">
                              <AvatarFallback style={{ background: 'hsl(var(--primary) / 0.1)', color: 'hsl(var(--primary))', fontSize: '0.75rem' }}>
                                {t.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm font-medium">{t.full_name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 hidden sm:table-cell">
                          <span className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>{t.email}</span>
                        </td>
                        <td className="py-3 px-4 hidden md:table-cell">
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'hsl(var(--accent))' }}>Teacher</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium status-${t.status}`}>
                            {t.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem className="text-red-600" onClick={() => deactivateTeacher(t.id)}>
                                <UserX className="w-4 h-4 mr-2" /> Deactivate
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardShell>
  );
}
